/**
 * ChecklistBuilderModal - Manual checklist creation
 * Creates custom checklist templates via useOperationalChecklists hook
 */

import React, { useState } from 'react';
import { X, Plus, Trash2, GripVertical, AlertCircle, CheckCircle } from 'lucide-react';
import { useOperationalChecklists } from '../../../../hooks/domain/operations/useOperationalChecklists';
import { CHECKLIST_CATEGORIES } from '../../../../config/operationsConfig';
import Dropdown from '../../../common/Dropdown';

const ChecklistBuilderModal = ({ isOpen, onClose, onComplete }) => {
  const { createCustomTemplate } = useOperationalChecklists();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'operational',  // Changed default to operational
    reference: '',
    // Linking configuration
    canLinkToVoyage: true,
    autoLogToLogbook: true,
    requiresSignature: false
  });

  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');

  const [items, setItems] = useState([
    { id: 'item_1', text: '', required: true, guidance: '' }
  ]);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Add new item
  const addItem = () => {
    const newId = `item_${Date.now()}`;
    setItems([...items, { id: newId, text: '', required: false, guidance: '' }]);
  };

  // Remove item
  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  // Update item
  const updateItem = (id, field, value) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim() || formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    const validItems = items.filter(item => item.text.trim());
    if (validItems.length === 0) {
      newErrors.items = 'At least one checklist item is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);

    try {
      // Filter out empty items and clean up
      const cleanItems = items
        .filter(item => item.text.trim())
        .map((item, index) => ({
          id: `check_${index + 1}`,
          text: item.text.trim(),
          required: item.required,
          ...(item.guidance.trim() && { guidance: item.guidance.trim() })
        }));

      const templateData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        category: formData.category,
        reference: formData.reference.trim() || null,
        items: cleanItems,
        // Linking and logging options
        canLinkTo: formData.canLinkToVoyage ? ['voyage', 'logbook'] : [],
        autoLogOnComplete: formData.autoLogToLogbook,
        requiresSignature: formData.requiresSignature
      };

      const result = await createCustomTemplate(templateData);

      if (result) {
        onComplete(result);
      } else {
        setErrors({ submit: 'Permission denied. Only Skippers and Fleet Managers can create checklists.' });
      }
    } catch (error) {
      console.error('Error creating checklist:', error);
      setErrors({ submit: 'Failed to create checklist. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Create New Checklist</h2>
              <p className="text-sm text-slate-500">Build a custom operational checklist</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Error banner */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {errors.submit}
              </div>
            )}

            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-700">Checklist Details</h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Cargo Loading Checks"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                    errors.name ? 'border-red-300 bg-red-50' : 'border-slate-300'
                  }`}
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of when to use this checklist..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category
                  </label>
                  <Dropdown
                    value={formData.category.startsWith('custom_') ? '__custom__' : formData.category}
                    onChange={(value) => {
                      if (value === '__custom__') {
                        setShowCustomCategory(true);
                      } else {
                        setFormData({ ...formData, category: value });
                        setShowCustomCategory(false);
                      }
                    }}
                    options={[
                      { value: 'operational', label: 'Operational' },
                      { value: 'navigation', label: 'Navigation' },
                      { value: 'emergency', label: 'Emergency' },
                      { value: 'sms', label: 'SMS / Permit' },
                      { value: '__custom__', label: '+ Create New Category...' }
                    ]}
                    width="w-full"
                    placeholder="Select category..."
                  />
                  {showCustomCategory && (
                    <input
                      type="text"
                      value={customCategoryName}
                      onChange={(e) => {
                        setCustomCategoryName(e.target.value);
                        // Convert to lowercase slug for category ID
                        const slug = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                        setFormData({ ...formData, category: `custom_${slug}` });
                      }}
                      placeholder="Enter category name..."
                      className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      autoFocus
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Reference (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    placeholder="e.g., SOP-14"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Linking Options */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Options</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.canLinkToVoyage}
                      onChange={(e) => setFormData({ ...formData, canLinkToVoyage: e.target.checked })}
                      className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    Can be linked to active voyage
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.autoLogToLogbook}
                      onChange={(e) => setFormData({ ...formData, autoLogToLogbook: e.target.checked })}
                      className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    Auto-log completion to Main Logbook
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiresSignature}
                      onChange={(e) => setFormData({ ...formData, requiresSignature: e.target.checked })}
                      className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    Requires signature on completion
                  </label>
                </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-700">
                  Checklist Items <span className="text-red-500">*</span>
                </h3>
                <button
                  onClick={addItem}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Item
                </button>
              </div>

              {errors.items && (
                <p className="text-xs text-red-600">{errors.items}</p>
              )}

              <div className="space-y-3">
                {items.map((item, index) => (
                  <div
                    key={item.id}
                    className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2"
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex items-center gap-1 text-slate-400 pt-2">
                        <GripVertical className="h-4 w-4" />
                        <span className="text-xs font-medium">{index + 1}</span>
                      </div>

                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.text}
                          onChange={(e) => updateItem(item.id, 'text', e.target.value)}
                          placeholder="Check item text..."
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      </div>

                      <button
                        onClick={() => removeItem(item.id)}
                        disabled={items.length === 1}
                        className={`p-2 rounded-lg transition-colors ${
                          items.length === 1
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                        }`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* FT-110: Required toggle removed - all checks are treated equally */}
                    <div className="flex items-center gap-4 ml-8">
                      <input
                        type="text"
                        value={item.guidance}
                        onChange={(e) => updateItem(item.id, 'guidance', e.target.value)}
                        placeholder="Guidance note (optional)"
                        className="flex-1 px-2 py-1 border border-slate-200 rounded text-xs text-slate-600 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <p className="text-xs text-slate-500">
              {items.filter(i => i.text.trim()).length} item(s) configured
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:bg-cyan-400 transition-colors"
              >
                {saving ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Create Checklist
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistBuilderModal;
